// ABOUTME: Modal for creating a new location bundle template with name and carrier conditions
// ABOUTME: Manages dynamic carrier list with quantity inputs for bundle template configuration

import { Box, InputBase, useTheme, IconButton } from "@mui/material"
import { useEffect, useState } from "react";

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import { tokens } from "../../../theme";
import SHModal from "../../theme/Modal/SHModal";
import Shbutton from "../../theme/buttons/Shbutton";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";
import ButtonGroup from "../../theme/buttons/ButtonGroup";
import { useTranslation } from "react-i18next";
import { showToastMessagePostRequest, showToastMessageRequestError, showToastMessage } from "../../../utils/toasts";
import * as yup from "yup";
import ShValidatedInput from "../../theme/inputs/ShValidatedInput";
import { yupRequired } from "../../../utils/validation";
import { InputDropdown } from "../../theme/dropdowns/InputDropdown";
import { useGetCarriersSelectQuery } from "../../../newapi/carrier/carrierSlice";
import ShValidatedNumberInput from "../../theme/inputs/ShValidatedNumberInput";
import { useAddLocationBundleTemplateMutation } from "../../../newapi/warehouse/locationBundleTemplateSlice";

const MAddLocationBundleTemplate = (props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [templateData, setTemplateData] = useState({
        name: "",
    });

    const [carriersList, setCarriersList] = useState([{
        id: 0,
        rowIndex: 0,
        carrierId: 0,
        quantity: 1
    }]);

    const { data: carriers, carriersLoading } = useGetCarriersSelectQuery();

    useEffect(() => {
        if (props.open) {
            setTemplateData({
                name: "",
                carryingCapacity: 0
            });
            setCarriersList([{
                id: 0,
                rowIndex: 0,
                carrierId: 0,
                quantity: 1
            }]);
        }
    }, [props.open]);

    const [fieldErrors, setFieldErrors] = useState({});

    const rules = {
        name: yupRequired('name', t),
    };

    const schema = yup.object().shape(rules);

    const [isLoading, setIsLoading] = useState(false);

    const handleClose = () => {
        setTemplateData({
            name: "",
            carryingCapacity: 0
        });
        setCarriersList([{
            id: 0,
            rowIndex: 0,
            carrierId: 0,
            quantity: 1
        }]);
        setFieldErrors({});
        props.handleClose();
    }

    const [addTemplate,{isLoading: templateLoading}] = useAddLocationBundleTemplateMutation();

    const SaveTemplate = async () => {
        try {
            if (templateLoading) return;
            setFieldErrors({});
            await schema.validate(templateData, { abortEarly: false });

            // Check for carriers
            const validCarriers = carriersList.filter(c => c.carrierId > 0 && c.quantity > 0);
            if (carriersList.some(c => c.carrierId > 0 && (!c.quantity || c.quantity <= 0))) {
                showToastMessage("error", t("all_carriers_must_have_quantity"));
                return;
            }

            const requestData = {
                name: templateData.name,
                carryingCapacity: templateData.carryingCapacity,
                conditions: {
                    carriers: validCarriers.map(c => ({
                        id: c.carrierId,
                        quantity: c.quantity
                    }))
                }
            };

            const data = await addTemplate(requestData).unwrap();
            showToastMessagePostRequest(t, data);

            handleClose();
        } catch (err) {
            setIsLoading(false);
            if (err.name === 'ValidationError') {
                const errors = err.inner.reduce((acc, currentError) => ({
                  ...acc,
                  [currentError.path]: currentError.message
                }), {});

                setFieldErrors(errors);
            } else {
                showToastMessageRequestError(t("create_failed"), t, err);
            }
        }
    };

    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    const handleChange = (key, value) => {
        setTemplateData(prev => ({
          ...prev,
          [key]: value
        }));
        setFieldErrors({
            ...fieldErrors,
            [key]: ''
        });
    };

    const handleCarrierSelect = (rowIndex, selected) => {
        rowIndex = parseInt(rowIndex);
        // Check if selected carrier is already in the list
        const isDuplicate = carriersList.some(line => line.carrierId === selected.id);
        if (isDuplicate) {
            showToastMessage("error", t("carrier_already_in_list"));
            return;
        }

        setCarriersList(prev => prev.map((line, index) => {
            if (line.rowIndex === rowIndex) {
                return {
                    rowIndex: rowIndex,
                    carrierId: selected.id,
                    carrierName: selected.name,
                    quantity: 1,
                    id: selected.id
                };
            }
            return line;
        }));
    };

    useEffect(() => {
        const lastItem = carriersList[carriersList.length - 1];
        if (lastItem.carrierId > 0) {
            setCarriersList(prev => [...prev, {
                id: 0,
                rowIndex: carriersList.length,
                carrierId: 0,
                quantity: 1
            }]);
        }
    }, [carriersList]);

    const handleDeleteCarrierRow = (rowIndex) => {
        setCarriersList(prev => prev.filter(line => line.rowIndex !== rowIndex));
    };

    const handleCarrierChange = (rowIndex, key, value) => {
        setCarriersList(prev => prev.map((line, index) => {
            if (line.rowIndex === rowIndex) {
                return {
                    ...line,
                    [key]: value
                };
            }
            return line;
        }));
    };

    const labelWidth = 120;

    return(
        <SHModal
            open={props.open}
            onClose={handleClose}
        >
            <Box
                sx={{
                    textAlign:"center",
                    paddingY:4,
                    paddingX:5,
                    display:"flex",
                    flexDirection:"column",
                    gap:1
                }}>
                <Subheading>{t("new_location_bundle_template")}</Subheading>
            </Box>

            {/* BODY */}
            <Box
                sx={{
                    background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    padding:3,
                    borderTopLeftRadius:16,
                    borderTopRightRadius:16,
                    display:"flex",
                    flexDirection:"column",
                    gap:2
                }}
            >
                <Box
                    sx={{
                        width:1,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    <Box
                        sx={{
                            width:labelWidth,
                            flexShrink:0,
                        }}
                    >
                        <Text light>{t("name")}</Text>
                    </Box>

                    <Box
                        sx={{
                            flex:1,
                            borderRadius:4,
                            display:"flex",
                        }}
                    >
                        <ShValidatedInput
                            height={40}
                            name="name"
                            value={templateData?.name}
                            onChange={handleChange}
                            error={fieldErrors.name}
                        />
                    </Box>
                </Box>
                <Box
                    sx={{
                        width:1,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    <Box
                        sx={{
                            width:labelWidth,
                            flexShrink:0,
                        }}
                    >
                        <Text light>{t("carrying_capacity")} ({t("kg")})</Text>
                    </Box>

                    <Box
                        sx={{
                            flex:1,
                            borderRadius:4,
                            display:"flex",
                        }}
                    >
                        <ShValidatedNumberInput
                            btnSize={"24"}
                            name="carryingCapacity"
                            value={templateData?.carryingCapacity ?? 0}
                            onChange={handleChange}
                            error={fieldErrors.carryingCapacity}
                        />
                    </Box>
                </Box>

                <Box sx={{ mt: 2 }}>
                    <Text light>{t("carriers")}</Text>
                </Box>

                {carriersList && carriersList.length > 0 && carriersList.map((carrierLine, index) => (
                    <Box key={"add-carrier-line-"+index}
                        sx={{
                            display:"flex",
                            alignItems:"center",
                            gap:2
                        }}
                    >
                        <Box
                            sx={{
                                flex:2
                            }}
                        >
                            <InputDropdown
                                searchOptions={{
                                    enabled: true,
                                    placeholder: t("search")
                                }}
                                styles={{
                                    minWidth:200
                                }}
                                displayName={"name"}
                                onChange={(selected) => handleCarrierSelect(index, carriers[selected])}
                                noSelection={t("choose_carrier")}
                                options={carriers ?? []}
                                closeOnSelection
                            />
                        </Box>
                        <Box
                            sx={{
                                flex:1
                            }}
                        >
                            <Box display="flex"
                                height="40px"
                                borderRadius="20px"
                                backgroundColor={colors.grey[100]}
                            >
                                <InputBase
                                    sx={{
                                        px:2,
                                        flex:1,
                                        color:colors.txt["primary"],
                                        lineHeight:"32px",
                                    }}
                                    type="number"
                                    placeholder={t("quantity")}
                                    value={carrierLine.quantity}
                                    onChange={(e) => handleCarrierChange(index, 'quantity', parseInt(e.target.value) || 0)}
                                    disabled={carrierLine.carrierId === 0}
                                />
                            </Box>
                        </Box>
                        { index > 0 ?
                            <Box
                                sx={{
                                    width:40
                                }}
                            >
                                <IconButton color="red" type="button" onClick={() => handleDeleteCarrierRow(index)} sx={{p:1}}>
                                    <DeleteIcon/>
                                </IconButton>
                            </Box>
                        :
                        carriersList.length > 0 ?
                            <Box
                                sx={{
                                    width:40,
                                }}
                            >
                            </Box>
                            : null
                        }
                    </Box>
                ))}
            </Box>
            <Box
                component="footer"
                sx={{
                    display:"block",
                    width:1,
                    paddingBottom: isStandAlone ? "32px" : "16px",
                    display:"flex",
                    alignItems:"center",
                    paddingRight:3,
                    zIndex:1058,
                    position:"relative",
                    paddingTop:2,
                    background:`${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                    borderBottomLeftRadius:24,
                    borderBottomRightRadius:24,
                    justifyContent:"space-between",
                    "@media screen and (max-width: 47.9375em)" : {
                        paddingRight:0,
                    }
                }}
            >
                <Shbutton isCancel={true} className={"always-visible"} onClick={handleClose} color="primary" variant="text" startIcon={<ArrowRightAltIcon/>}>{t("cancel")}</Shbutton>
                <ButtonGroup version={2}>
                    <Shbutton isLoading={isLoading} className={"group-btn"} color={"green"}
                    variant="contained"
                    onClick={() => SaveTemplate()}
                    >{t("add")}</Shbutton>
                </ButtonGroup>
            </Box>
        </SHModal>
    )
}

export default MAddLocationBundleTemplate;
